import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSignIn, useSignUp } from "@clerk/clerk-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import toast from "react-hot-toast";
import Logo from "../../assets/images/logo.png";
import googleImage from "../../assets/images/google.svg";
import appleImage from "../../assets/images/apple.svg";
import facebookImage from "../../assets/images/facebook.svg";
import { trackEvent } from "../../utils/analytics";

const isValidEmail = (value) =>
    /^[a-z0-9._%+-]{1,64}@(?:[a-z0-9-]{1,63}\.){1,4}[a-z]{2,5}$/i.test(value.trim());

const is18PlusDate = (dob) => {
    if (!dob) return true;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age >= 18;
};

const phoneDigits = (value) => (value || "").replace(/\D/g, "");

const SimplifiedAuthCard = ({
    initialMode = "signin",
    redirectComplete = "/user/creator-list",
    redirectUsername = "",
    afterAuthPath = "",
    submitLabel,
    introText,
    showLogo = true,
}) => {
    const { signIn, isLoaded, setActive } = useSignIn();
    const { signUp, setActive: setActiveSignUp } = useSignUp();
    const navigate = useNavigate();

    const [step, setStep] = useState("oauth");
    const [isRegister, setIsRegister] = useState(initialMode === "signup");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [dob, setDob] = useState(null);
    const [otp, setOtp] = useState("");
    const [resetCode, setResetCode] = useState("");
    const [showOtp, setShowOtp] = useState(false);
    const [resetSent, setResetSent] = useState(false);
    const [togglePassword, setTogglePassword] = useState(false);
    const [optInCommunity, setOptInCommunity] = useState(false);
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const destination = afterAuthPath
        || (redirectUsername
            ? `/user/creator-list?username=${redirectUsername}`
            : redirectComplete);

    const primaryLabel = submitLabel || (isRegister ? "Sign Up" : "Sign In");

    const rememberRedirect = () => {
        if (afterAuthPath) {
            sessionStorage.setItem("tlk_after_auth", afterAuthPath);
            sessionStorage.removeItem("redirectUsername");
            return;
        }
        sessionStorage.removeItem("tlk_after_auth");
        if (redirectUsername) {
            sessionStorage.setItem("redirectUsername", redirectUsername);
        } else {
            sessionStorage.removeItem("redirectUsername");
        }
    };

    const switchMode = (nextRegister) => {
        setIsRegister(nextRegister);
        setError("");
        setShowOtp(false);
        setOtp("");
        setResetSent(false);
        setResetCode("");
        setNewPassword("");
        if (step === "reset") {
            setStep("email");
        }
    };

    const handleOAuth = async (strategy) => {
        if (!isLoaded || isSubmitting) return;
        setError("");
        rememberRedirect();
        try {
            await signIn.authenticateWithRedirect({
                strategy,
                redirectUrl: "/sso-callback",
                redirectUrlComplete: destination,
            });
        } catch (err) {
            try {
                await signUp.authenticateWithRedirect({
                    strategy,
                    redirectUrl: "/sso-callback",
                    redirectUrlComplete: destination,
                });
            } catch (signupErr) {
                setError(signupErr.errors?.[0]?.longMessage || "Social login failed");
            }
        }
    };

    const handleEmailContinue = (event) => {
        event.preventDefault();
        setError("");
        if (!isValidEmail(email)) {
            setError("Please enter a valid email address.");
            return;
        }
        setStep("email");
    };

    const handleRegister = async () => {
        if (isSubmitting) return;
        setError("");

        if (dob && !is18PlusDate(dob)) {
            setError("You must be 18+ to continue.");
            return;
        }

        if (!password) {
            setError("Please enter a password.");
            return;
        }

        const digitsOnly = phoneDigits(phoneNumber);
        const hasPhone = digitsOnly.length > 1;
        if (hasPhone && digitsOnly.length < 11) {
            setError("Please enter a valid phone number.");
            return;
        }

        if (!isLoaded) return;
        setIsSubmitting(true);
        try {
            const payload = {
                emailAddress: email.trim(),
                password,
                unsafeMetadata: {
                    smsOptIn: optInCommunity,
                },
            };
            if (firstName.trim()) payload.firstName = firstName.trim();
            if (lastName.trim()) payload.lastName = lastName.trim();
            if (hasPhone) payload.phoneNumber = `+${digitsOnly}`;

            const created = await signUp.create(payload);
            // trackEvent('email_captured', { method: 'email' });

            if (created.status === "complete") {
                await setActiveSignUp({ session: created.createdSessionId });
                window.location.href = destination;
                return;
            }

            await created.prepareEmailAddressVerification({
                strategy: "email_code",
            });

            navigate(
                redirectUsername
                    ? `/otp-verification?username=${redirectUsername}`
                    : "/otp-verification"
            );
        } catch (err) {
            const code = err.errors?.[0]?.code;
            if (code === "session_exists") {
                window.location.href = destination;
                return;
            }
            setError(err.errors?.[0]?.longMessage || "Registration failed");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleLogin = async () => {
        setError("");
        if (!isLoaded) return;
        if (!password) {
            setError("Please enter your password.");
            return;
        }

        try {
            const result = await signIn.create({
                identifier: email.trim(),
                password,
            });

            if (result.status === "complete") {
                await setActive({ session: result.createdSessionId });
                window.location.href = destination;
            } else if (result.status === "needs_second_factor") {
                await signIn.prepareSecondFactor({ strategy: "email_code" });
                setShowOtp(true);
            }
        } catch (err) {
            if (err.errors?.[0]?.code === "user_banned") {
                toast.error("Your account has been suspended");
                setError(
                    "Your access to TLK has been suspended for violating our Terms of Service. If you believe this was a mistake, contact our support team and we'll review your account"
                );
            } else {
                setError(err.errors?.[0]?.longMessage || "Login failed");
            }
        }
    };

    const handleVerifyOtp = async () => {
        setError("");
        try {
            const result = await signIn.attemptSecondFactor({
                strategy: "email_code",
                code: otp,
            });

            if (result.status === "complete") {
                await setActive({ session: result.createdSessionId });
                window.location.href = destination;
            } else {
                setError("Verification failed. Please try again.");
            }
        } catch (err) {
            setError(err.errors?.[0]?.message || "OTP verification failed");
        }
    };

    const handleSendResetCode = async () => {
        if (!isLoaded) return;
        setError("");
        try {
            await signIn.create({
                strategy: "reset_password_email_code",
                identifier: email.trim(),
            });
            setResetSent(true);
            setStep("reset");
        } catch (err) {
            setError(err.errors?.[0]?.longMessage || "Could not send reset code");
        }
    };

    const handleResetPassword = async (event) => {
        event.preventDefault();
        setError("");
        if (!resetCode || !newPassword) {
            setError("Enter the reset code and a new password.");
            return;
        }
        try {
            const result = await signIn.attemptFirstFactor({
                strategy: "reset_password_email_code",
                code: resetCode,
                password: newPassword,
            });
            if (result.status === "complete") {
                await setActive({ session: result.createdSessionId });
                window.location.href = destination;
            } else {
                setError("Password reset failed. Please try again.");
            }
        } catch (err) {
            setError(err.errors?.[0]?.longMessage || "Password reset failed");
        }
    };

    const handleEmailSubmit = (event) => {
        event.preventDefault();
        if (isRegister) {
            handleRegister();
            return;
        }
        if (showOtp) {
            handleVerifyOtp();
            return;
        }
        handleLogin();
    };

    const legalLinks = (
        <>
            <Link to="/user/terms" target="_blank" rel="noopener noreferrer">
                Terms and Conditions
            </Link>
            {", "}
            <Link to="/user/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
            </Link>
            {", "}
             <Link to="/complaints-policy" target="_blank" rel="noopener noreferrer">
                Complaints & Content Removal Policy
            </Link>
            {", and "}
            <Link to="/community-guidelines" target="_blank" rel="noopener noreferrer">
                Community Guidelines
            </Link>
        </>
    );

    if (!isLoaded) {
        return (
            <div className="chat-landing-card simplified-auth-card">
                <div className="auth-card-loading">
                    <div className="spin-ring">
                        <div className="ring"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="chat-landing-card simplified-auth-card">
            {showLogo && (
                <div className="card-brand-logo">
                    <img src={Logo} alt="TLK" />
                </div>
            )}

            {introText && <p className="card-intro">{introText}</p>}

            {step !== "oauth" && (
                <button
                    type="button"
                    className="step-back"
                    onClick={() => {
                        setError("");
                        setShowOtp(false);
                        setResetSent(false);
                        setStep("oauth");
                    }}
                >
                    <ArrowBackIcon fontSize="small" />
                    Back
                </button>
            )}

            {step === "oauth" && (
                <form onSubmit={handleEmailContinue}>
                    <div className="oauth-stack">
                        <button
                            type="button"
                            className="oauth-google-btn"
                            onClick={() => handleOAuth("oauth_google")}
                        >
                            <img src={googleImage} alt="" />
                            Continue with Google
                        </button>
                        {/* <div className="oauth-secondary"> */}
                            <button
                                type="button"
                                className="oauth-google-btn"
                                onClick={() => handleOAuth("oauth_apple")}
                            >
                                <img src={appleImage} alt="" className="apple-icon" />
                                Continue with Apple
                            </button>
                            {/* <button
                                type="button"
                                className="oauth-alt-btn"
                                onClick={() => handleOAuth("oauth_facebook")}
                            >
                                <img src={facebookImage} alt="" />
                                Facebook
                            </button> */}
                        {/* </div> */}
                    </div>

                    <div className="email-continue-block">
                        <div className="divider-row">
                            <span>Or email</span>
                        </div>

                        <div className="field-group">
                            <label htmlFor="auth-email">Email address</label>
                            <input
                                id="auth-email"
                                type="email"
                                placeholder="john@example.com"
                                value={email}
                                autoComplete="email"
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>

                        {error && <p className="field-error">{error}</p>}

                        <button type="submit" className="submit-btn">
                            Continue
                        </button>
                    </div>
                </form>
            )}

            {step === "email" && (
                <form onSubmit={handleEmailSubmit}>
                    <p className="email-preview">{email}</p>

                    {isRegister && (
                        <>
                            <div className="field-group">
                                <div className="field-row-2">
                                    <div>
                                        <label htmlFor="auth-first-name">First name</label>
                                        <input
                                            id="auth-first-name"
                                            type="text"
                                            placeholder="John"
                                            value={firstName}
                                            autoComplete="given-name"
                                            onChange={(e) => setFirstName(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="auth-last-name">Last name</label>
                                        <input
                                            id="auth-last-name"
                                            type="text"
                                            placeholder="Doe"
                                            value={lastName}
                                            autoComplete="family-name"
                                            onChange={(e) => setLastName(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="field-group phone-input-div">
                                <label htmlFor="auth-phone">
                                    Phone number <span className="optional-label">Optional</span>
                                </label>
                                <PhoneInput
                                    country={"us"}
                                    value={phoneNumber}
                                    onChange={(phone) => setPhoneNumber(phone)}
                                    inputProps={{ id: "auth-phone" }}
                                />
                            </div>

                            <div className="field-group datePicker-div">
                                <label htmlFor="auth-dob">
                                    Date of birth <span className="optional-label">Optional</span>
                                </label>
                                <DatePicker
                                    id="auth-dob"
                                    selected={dob}
                                    onChange={(date) => setDob(date)}
                                    placeholderText="MM/DD/YYYY"
                                    dateFormat="MM/dd/yyyy"
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    maxDate={new Date()}
                                />
                            </div>
                        </>
                    )}

                    <div className="field-group">
                        <label htmlFor="auth-password">Password</label>
                        <div className="input-with-icon">
                            <input
                                id="auth-password"
                                type={!togglePassword ? "password" : "text"}
                                placeholder="At least 8 characters"
                                value={password}
                                autoComplete={isRegister ? "new-password" : "current-password"}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                            <button
                                type="button"
                                onClick={() => setTogglePassword(!togglePassword)}
                                className="icon-btn"
                                aria-label={togglePassword ? "Hide password" : "Show password"}
                            >
                                {togglePassword ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
                            </button>
                        </div>
                    </div>

                    {!isRegister && !showOtp && (
                        <div className="forgot-row">
                            <button type="button" className="text-link-btn" onClick={handleSendResetCode}>
                                Forgot password?
                            </button>
                        </div>
                    )}

                    {showOtp && (
                        <div className="field-group">
                            <label htmlFor="auth-otp">Verification code</label>
                            <input
                                id="auth-otp"
                                type="text"
                                placeholder="Enter OTP sent to your email"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                            />
                        </div>
                    )}

                    {isRegister && (
                        <div className="consent-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={optInCommunity}
                                    onChange={(e) => setOptInCommunity(e.target.checked)}
                                />
                                <span>
                                    Stay updated with creator announcements, platform news, and special offers
                                </span>
                            </label>
                        </div>
                    )}

                    {error && <p className="field-error">{error}</p>}

                    <button
                        type="submit"
                        className="submit-btn"
                        disabled={isSubmitting}
                    >
                        {showOtp ? "Verify OTP" : primaryLabel}
                    </button>
                </form>
            )}

            {step === "reset" && (
                <form onSubmit={handleResetPassword}>
                    <p className="card-intro">
                        {resetSent
                            ? `Enter the code sent to ${email} and choose a new password.`
                            : "Reset your password"}
                    </p>
                    <div className="field-group">
                        <label htmlFor="auth-reset-code">Reset code</label>
                        <input
                            id="auth-reset-code"
                            type="text"
                            placeholder="Email code"
                            value={resetCode}
                            onChange={(e) => setResetCode(e.target.value)}
                        />
                    </div>
                    <div className="field-group">
                        <label htmlFor="auth-new-password">New password</label>
                        <input
                            id="auth-new-password"
                            type="password"
                            placeholder="At least 8 characters"
                            value={newPassword}
                            autoComplete="new-password"
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                    </div>
                    {error && <p className="field-error">{error}</p>}
                    <button type="submit" className="submit-btn">
                        Reset password
                    </button>
                </form>
            )}

            {step !== "reset" && (
                <p className="switch-mode-text">
                    {isRegister ? (
                        <>
                            Already have an account?{" "}
                            <button type="button" onClick={() => switchMode(false)}>
                                Sign In
                            </button>
                        </>
                    ) : (
                        <>
                            Don&apos;t have an account?{" "}
                            <button type="button" onClick={() => switchMode(true)}>
                                Sign Up
                            </button>
                        </>
                    )}
                </p>
            )}

            {step === "oauth" && isRegister && (
                <p className="oauth-legal">
                    By continuing, you agree to the {legalLinks}, and confirm you are 18 or older.
                </p>
            )}
        </div>
    );
};

export default SimplifiedAuthCard;
